/**
 * Phase 13 PHOTOSTRIP-6.1 + D-03 + D-13 + D-14 — PM web container mapping function.
 *
 * Return type narrowing (V-M2 mitigation 1):
 *   - Excludes kind='local-only' (PM never emits — only Worker does).
 *   - Strips onRetry from broken arm (PM must NEVER trigger retry).
 *
 * The TS distributive conditional types in `PmPhotoTileState` (photoTileState.ts:30-35)
 * lock both narrowings at compile time. This module's `mapPmEntryToTileState()` returns
 * `PmPhotoTileState` directly, so any accidental `local-only` emission or `onRetry`
 * assignment becomes a TS error in the calling container (PhotoStrip.tsx).
 *
 * Mapping rules (R5-002 / D9, audit 2026-07):
 *   - uploaded_at IS NULL     → kind: 'uploading'
 *   - uploaded_at NOT NULL    → kind: 'landed'
 *
 * The Phase-6 CR-04 `storage_path === null → broken` branch was DEAD CODE:
 * photo.storage_path is NOT NULL at the DB level and placeholder rows carry
 * a '.pending-…' sentinel string, so the predicate could never be true — and
 * the reachable failure shape (uploaded_at set but the JPEG missing from
 * /data/photos, e.g. after a DB-only restore) mapped to 'landed' and showed
 * a bare browser broken-image glyph. Per operator decision D9 the branch is
 * dropped and the thumb-404 case is handled by PhotoTile's <img> onError,
 * which flips the landed tile to the broken treatment.
 *
 * @SpecRef PHOTOSTRIP-6.1
 */
import type { PmPhotoEntry } from '../../../api/inspections';
import type { PmPhotoTileState } from './photoTileState';

export function mapPmEntryToTileState(p: PmPhotoEntry): PmPhotoTileState {
  if (p.uploaded_at === null) {
    return { kind: 'uploading', photoId: p.id };
  }
  // onRetry intentionally never present — D-13 V-M2 mitigation 1: PM container
  // must never pass a retry handler; PmPhotoTileState's Omit<S, 'onRetry'>
  // narrowing makes it a compile-time error anyway.
  return { kind: 'landed', photoId: p.id };
}
